<template>
  <v-dialog v-if="show" v-model="show" persistent width="75%">
    <v-card class="black--text" color="cardbg">
      <v-card-title>
        <span class="headline font-weight-bold black--text">{{
          $t(dialogTitle)
        }}</span></v-card-title
      >
      <v-card-text>
        <v-form class="form-container" ref="doctorForm" v-model="formValid">
          <v-row no-gutters>
            <v-col class="col-12">
              <v-text-field
                v-model="doctorName"
                class="form-input"
                :placeholder="$t('add.default')"
                :rules="[
                  rules.required,
                  rules.inputLength,
                  rules.oneSpaceAllowed
                ]"
                :counter="maxInputLength"
                :disabled="!hasWritePerm"
                required
                regular
              >
                <span slot="prepend" class="title mt-1">{{
                  $t("hfManagement.name")
                }}</span>
              </v-text-field>
            </v-col>
            <v-col class="col-12">
              <v-text-field
                v-model="doctorSurname"
                class="form-input"
                :placeholder="$t('add.default')"
                :rules="[
                  rules.required,
                  rules.inputLength,
                  rules.oneSpaceAllowed
                ]"
                :counter="maxInputLength"
                :disabled="!hasWritePerm"
                required
                regular
              >
                <span slot="prepend" class="title mt-1">{{
                  $t("hfManagement.surname")
                }}</span>
              </v-text-field>
            </v-col>
            <v-col class="col-12">
              <v-autocomplete
                v-model="selectedServiceList"
                :items="fullServiceList"
                :placeholder="$t('add.default')"
                return-object
                :rules="[() => selectedServiceList?.length > 0]"
                item-text="nameByLang"
                :no-data-text="loadingServicesMsg"
                class="input-multi-select"
                multiple
                chips
                deletable-chips
                hide-details
                auto-select-first
                :menu-props="{
                  closeOnContentClick: true,
                  maxWidth: 'min-content'
                }"
                :disabled="!hasWritePerm"
              >
                <span slot="prepend" class="title mt-1">{{
                  $t("hfManagement.serviceList")
                }}</span>
                <template v-slot:selection="data">
                  <v-tooltip class="service-item" color="primary" bottom>
                    <template v-slot:activator="{ on }">
                      <v-chip
                        v-on="on"
                        :input-value="data.selected"
                        class="service-chip text-uppercase white--text my-1 mr-1"
                        small
                        color="primary"
                        :disabled="!hasWritePerm"
                        :close="hasWritePerm"
                        @click:close="removeServiceChip(data.item)"
                        ><span class="font-weight-bold text-truncate">
                          {{ data.item.nameByLang }}
                        </span></v-chip
                      >
                    </template>
                    <span class="font-weight-bold text-uppercase caption">
                      {{ data.item.nameByLang }}
                    </span>
                  </v-tooltip>
                </template>
                <template v-slot:item="data">
                  <v-list-item-content class="text-uppercase">{{
                    data.item.nameByLang
                  }}</v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>
      <v-card-actions>
        <v-row class="no-gutters px-3">
          <v-col v-if="hasDeleteAction && hasWritePerm" class="col-auto">
            <v-btn
              class="caption font-weight-bold ma-1"
              color="primary"
              rounded
              depressed
              @click="openDeleteDoctorDialog"
              >{{ $t("deleteButton") }}</v-btn
            >
          </v-col>
          <v-col class="col-auto ml-md-auto">
            <v-btn
              class="caption font-weight-bold ma-1"
              color="primary"
              outlined
              rounded
              depressed
              @click="show = false"
              >{{ $t("backButton") }}</v-btn
            >
          </v-col>
          <v-col v-if="hasWritePerm" class="col-auto">
            <v-btn
              class="caption font-weight-bold ma-1"
              color="primary"
              rounded
              depressed
              @click="saveDoctor"
              :disabled="!formValid"
              >{{ $t("saveButton") }}</v-btn
            >
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import findIndex from "lodash/findIndex";
import orderBy from "lodash/orderBy";

import ValidationRules from "../../views/validationRules";
let vrules = new ValidationRules();

export default {
  props: {
    hasWritePerm: {
      type: Boolean,
      required: true
    },
    visible: {
      type: Boolean,
      default: false
    },
    dialogTitle: {
      type: String,
      required: false
    },
    doctor: {
      type: Object,
      required: true
    },
    serviceList: {
      type: Array,
      required: true
    },
    initFormValid: {
      type: Boolean,
      required: true
    },
    hasDeleteAction: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  watch: {
    visible() {
      this.$nextTick(() => {
        if (this.visible) this.$refs.doctorForm.resetValidation();
      });
    }
  },
  data() {
    return {
      formValid: this.initFormValid,
      rules: vrules,
      maxInputLength: 50,
      loadingServicesMsg: `${this.$t("hfManagement.noServiceMsg")}`
    };
  },
  created() {
    this.initialize();
  },
  computed: {
    show: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    doctorName: {
      get() {
        return this.doctor.name;
      },
      set(name) {
        this.$emit("setdoctorname", name);
      }
    },
    doctorSurname: {
      get() {
        return this.doctor.surname;
      },
      set(surname) {
        this.$emit("setdoctorsurname", surname);
      }
    },
    selectedServiceList: {
      get() {
        return this.serviceList;
      },
      set(serviceList) {
        let sortedServiceList = orderBy(
          serviceList,
          service => service.nameByLang.toLowerCase(),
          "asc"
        );
        this.$emit("setservicelist", sortedServiceList);
      }
    },
    fullServiceList() {
      return this.$store.getters["doctors/serviceList"];
    }
  },
  methods: {
    initialize() {},
    removeServiceChip(item) {
      let index = findIndex(
        this.selectedServiceList,
        listItem => listItem.serviceId === item.serviceId
      );
      if (index !== -1) {
        this.selectedServiceList.splice(index, 1);
      }
    },
    saveDoctor() {
      if (this.$refs.doctorForm.validate()) {
        this.$emit("savedoctor");
      }
    },
    openDeleteDoctorDialog() {
      let deleteConfig = {
        title: "hfManagement.deleteDoctor",
        message: "hfManagement.confirmDeleteDoctorMsg",
        params: { msg: { name: this.doctorName, surname: this.doctorSurname } },
        cancelBtnLabel: "cancelButton",
        confirmBtnLabel: "deleteButton",
        confirmBtnClb: this.deleteDoctor
      };

      this.$store.commit("confirm/openConfirmDialog", deleteConfig, {
        root: true
      });
    },
    deleteDoctor() {
      this.$emit("deletedoctor");
    }
  }
};
</script>

<style scoped>
.form-input >>> .v-input__prepend-outer {
  width: 110px !important;
}
.input-multi-select >>> .v-input__prepend-outer {
  width: 110px !important;
}
.input-multi-select >>> .service-chip .v-chip__content {
  max-width: 380px;
}
</style>
